import axios from "axios";
import { action, makeObservable, observable, runInAction } from "mobx";
import { APIBlobResponse } from "../../../../models/NetworkResponse";
import RedemptionDashboardAPI from "../../api/dashboards/RedemptionDashboardAPI";
import {
  clampRedemptionPageSize,
  defaultJourneySort,
  defaultRedemptionAppliedFilters,
  defaultSortForView,
  RedemptionAppliedFilters,
  RedemptionFilters,
  RedemptionGroupBy,
  RedemptionProductView,
  RedemptionSort,
  REDEMPTION_AGG_SORT,
  REDEMPTION_JOURNEY_SORT,
  sortWhitelistForView,
  toRedemptionFilters,
} from "../../../../models/Dashboard/Redemption/RedemptionFilters";
import {
  RedemptionJourneyDrilldownResult,
  RedemptionProductDrilldownResult,
  RedemptionVariantMatrixResult,
} from "../../../../models/Dashboard/Redemption/RedemptionDrilldown";
import { RedemptionSummaryResult } from "../../../../models/Dashboard/Redemption/RedemptionSummary";
import { trackRedemptionAction, journeyStageLabel } from "../../../../analytics/redemptionTracking";

export type RedemptionDrawerKind = "journey" | "product" | "matrix";

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (data instanceof Blob) {
      return fallback;
    }
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message
    ) {
      return data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const triggerCsvDownload = (blob: Blob, filename?: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename || "ops-redemptions-export.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const sortIfAllowed = (
  sort: RedemptionSort | null,
  whitelist: readonly string[],
): RedemptionSort | undefined => {
  if (!sort) {
    return undefined;
  }
  if (!whitelist.includes(sort.column)) {
    return undefined;
  }
  return sort;
};

export class RedemptionDashboardStore {
  analyticsAPI: RedemptionDashboardAPI;
  appliedFilters: RedemptionAppliedFilters = defaultRedemptionAppliedFilters();

  summaryLoading = false;
  drawerLoading = false;
  exportLoading = false;
  summaryError: string | null = null;
  drawerError: string | null = null;
  exportError: string | null = null;

  summaryResult: RedemptionSummaryResult | null = null;
  journeyResult: RedemptionJourneyDrilldownResult | null = null;
  productResult: RedemptionProductDrilldownResult | null = null;
  matrixResult: RedemptionVariantMatrixResult | null = null;

  drawerOpen = false;
  drawerKind: RedemptionDrawerKind | null = null;
  journeyMetricId: string | null = null;
  /** Journey stage id for GA (created / sent / redeemed / …). */
  drawerJourneyStage: string | null = null;
  /** Human card title for GA (e.g. "Redeemed", product name). */
  drawerCardTitle: string | null = null;
  productId: number | null = null;
  productView: RedemptionProductView = "lines";
  groupBy: RedemptionGroupBy = "product";
  page = 1;
  pageSize = 25;
  sort: RedemptionSort | null = defaultSortForView("lines");

  private summarySeq = 0;
  private drawerSeq = 0;

  constructor() {
    this.analyticsAPI = new RedemptionDashboardAPI();
    makeObservable(this, {
      appliedFilters: observable.ref,
      summaryLoading: observable,
      drawerLoading: observable,
      exportLoading: observable,
      summaryError: observable,
      drawerError: observable,
      exportError: observable,
      summaryResult: observable.ref,
      journeyResult: observable.ref,
      productResult: observable.ref,
      matrixResult: observable.ref,
      drawerOpen: observable,
      drawerKind: observable,
      journeyMetricId: observable,
      drawerJourneyStage: observable,
      drawerCardTitle: observable,
      productId: observable,
      productView: observable,
      groupBy: observable,
      page: observable,
      pageSize: observable,
      sort: observable.ref,
      setAppliedFilters: action,
      fetchSummary: action,
      refreshDashboard: action,
      refreshData: action,
      changeSource: action,
      openJourney: action,
      openProduct: action,
      openMatrix: action,
      closeDrawer: action,
      changeProductView: action,
      changeGroupBy: action,
      changePage: action,
      changePageSize: action,
      changeSort: action,
      fetchDrawer: action,
      exportCsv: action,
    });
  }

  setAppliedFilters = (filters: RedemptionAppliedFilters) => {
    this.appliedFilters = filters;
  };

  fetchSummary = async (filters?: RedemptionAppliedFilters) => {
    const requestId = ++this.summarySeq;
    try {
      if (filters) {
        this.appliedFilters = filters;
      }
      this.summaryLoading = true;
      this.summaryError = null;

      const result = await this.analyticsAPI.getSummary(
        toRedemptionFilters(this.appliedFilters),
      );

      if (requestId !== this.summarySeq) {
        return;
      }

      runInAction(() => {
        this.summaryResult = result;
      });
    } catch (error) {
      if (requestId !== this.summarySeq) {
        return;
      }
      runInAction(() => {
        this.summaryError = getApiErrorMessage(
          error,
          "Failed to fetch ops redemptions summary.",
        );
        this.summaryResult = null;
      });
    } finally {
      if (requestId === this.summarySeq) {
        runInAction(() => {
          this.summaryLoading = false;
        });
      }
    }
  };

  refreshDashboard = async (filters?: RedemptionAppliedFilters) => {
    try {
      await this.fetchSummary(filters);
      if (this.drawerOpen) {
        runInAction(() => {
          this.page = 1;
        });
        await this.fetchDrawer();
      }
    } catch (error) {
      throw error;
    }
  };

  /**
   * Re-fetches the summary (and the open card, if any) with the already
   * applied filters. Keeps the open card's page / sort / view as-is.
   */
  refreshData = async () => {
    try {
      if (this.drawerOpen && this.drawerKind) {
        trackRedemptionAction("drawer_refresh", this.appliedFilters, {
          ...this.drawerTrackExtra(),
        });
        await Promise.all([this.fetchSummary(), this.fetchDrawer()]);
        return;
      }
      trackRedemptionAction("dashboard_refresh", this.appliedFilters);
      await this.fetchSummary();
    } catch (error) {
      throw error;
    }
  };

  changeSource = async (source: RedemptionAppliedFilters["source"]) => {
    try {
      if (this.appliedFilters.source === source) {
        return;
      }
      const previousSource = this.appliedFilters.source;
      const nextFilters = { ...this.appliedFilters, source };
      trackRedemptionAction("source_change", nextFilters, {
        previousSource,
        context: { previous_source: previousSource },
      });
      await this.refreshDashboard(nextFilters);
    } catch (error) {
      throw error;
    }
  };

  /**
   * Shared GA extras for the currently open card (title + kind + journey stage).
   *
   * @returns Label/context fields for trackRedemptionAction.
   */
  private drawerTrackExtra = () => {
    const journeyStage =
      this.drawerJourneyStage ||
      (this.journeyMetricId?.startsWith("journey:")
        ? this.journeyMetricId.replace(/^journey:/, "")
        : undefined);
    return {
      drawerKind: this.drawerKind,
      journeyStage: journeyStage || undefined,
      cardTitle: this.drawerCardTitle || undefined,
      productId: this.productId ?? undefined,
      productName:
        this.drawerKind === "product"
          ? this.drawerCardTitle || undefined
          : undefined,
      productView: this.productView,
      groupBy: this.groupBy,
      context: {
        drawer_kind: this.drawerKind,
        ...(this.drawerCardTitle
          ? { card_title: this.drawerCardTitle }
          : {}),
        ...(journeyStage ? { journey_stage: journeyStage } : {}),
        journey_metric_id: this.journeyMetricId || undefined,
        product_id: this.productId ?? undefined,
        product_view: this.productView,
        group_by: this.groupBy,
      },
    };
  };

  openJourney = async (metricId: string) => {
    try {
      if (!metricId.startsWith("journey:")) {
        return;
      }
      const journeyStage = metricId.replace(/^journey:/, "");
      const cardTitle = journeyStageLabel(journeyStage);
      const stageCount = this.summaryResult?.data?.journey?.stages?.find(
        (stage) => stage.stageId === journeyStage || stage.metricId === metricId,
      )?.count;
      trackRedemptionAction("journey_card_click", this.appliedFilters, {
        journeyStage,
        journeyCount: stageCount,
        cardTitle,
        drawerKind: "journey",
        context: {
          journey_metric_id: metricId,
          journey_stage: journeyStage,
          journey_stage_label: cardTitle,
          journey_count: stageCount,
          drawer_kind: "journey",
          card_title: cardTitle,
        },
      });
      this.drawerSeq += 1;
      this.drawerOpen = true;
      this.drawerKind = "journey";
      this.journeyMetricId = metricId;
      this.drawerJourneyStage = journeyStage;
      this.drawerCardTitle = cardTitle;
      this.productId = null;
      this.page = 1;
      this.pageSize = 25;
      this.sort = defaultJourneySort();
      this.drawerLoading = true;
      this.journeyResult = null;
      this.productResult = null;
      this.matrixResult = null;
      this.drawerError = null;
      this.exportError = null;
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  openProduct = async (productId: number, productName?: string) => {
    try {
      const cardTitle = productName?.trim() || `Product #${productId}`;
      trackRedemptionAction("product_row_click", this.appliedFilters, {
        productId,
        productName: cardTitle,
        cardTitle,
        drawerKind: "product",
        context: {
          product_id: productId,
          product_name: cardTitle,
          drawer_kind: "product",
          card_title: cardTitle,
        },
      });
      this.drawerSeq += 1;
      this.drawerOpen = true;
      this.drawerKind = "product";
      this.journeyMetricId = null;
      this.drawerJourneyStage = null;
      this.drawerCardTitle = cardTitle;
      this.productId = productId;
      this.productView = "lines";
      this.groupBy = "product";
      this.page = 1;
      this.pageSize = 25;
      this.sort = defaultSortForView("lines");
      this.drawerLoading = true;
      this.journeyResult = null;
      this.productResult = null;
      this.matrixResult = null;
      this.drawerError = null;
      this.exportError = null;
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  openMatrix = async () => {
    try {
      const cardTitle = "Variant matrix";
      trackRedemptionAction("variant_matrix_open", this.appliedFilters, {
        groupBy: "product",
        drawerKind: "matrix",
        cardTitle,
        context: {
          drawer_kind: "matrix",
          group_by: "product",
          card_title: cardTitle,
        },
      });
      this.drawerSeq += 1;
      this.drawerOpen = true;
      this.drawerKind = "matrix";
      this.journeyMetricId = null;
      this.drawerJourneyStage = null;
      this.drawerCardTitle = cardTitle;
      this.productId = null;
      this.groupBy = "product";
      this.page = 1;
      this.pageSize = 25;
      this.sort = { column: "units", dir: "desc" };
      this.drawerLoading = true;
      this.journeyResult = null;
      this.productResult = null;
      this.matrixResult = null;
      this.drawerError = null;
      this.exportError = null;
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  closeDrawer = () => {
    trackRedemptionAction("drawer_close", this.appliedFilters, {
      ...this.drawerTrackExtra(),
    });
    this.drawerSeq += 1;
    this.drawerOpen = false;
    this.drawerKind = null;
    this.journeyMetricId = null;
    this.drawerJourneyStage = null;
    this.drawerCardTitle = null;
    this.productId = null;
    this.productView = "lines";
    this.groupBy = "product";
    this.page = 1;
    this.pageSize = 25;
    this.sort = defaultSortForView("lines");
    this.journeyResult = null;
    this.productResult = null;
    this.matrixResult = null;
    this.drawerError = null;
    this.exportError = null;
    this.drawerLoading = false;
    this.exportLoading = false;
  };

  changeProductView = async (view: RedemptionProductView) => {
    try {
      if (this.drawerKind !== "product" || this.productView === view) {
        return;
      }
      const cardExtra = this.drawerTrackExtra();
      trackRedemptionAction("product_view_change", this.appliedFilters, {
        ...cardExtra,
        productView: view,
        previousProductView: this.productView,
        context: {
          ...cardExtra.context,
          product_view: view,
          previous_product_view: this.productView,
        },
      });
      this.productView = view;
      this.page = 1;
      this.sort = defaultSortForView(view);
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  changeGroupBy = async (groupBy: RedemptionGroupBy) => {
    try {
      if (this.drawerKind !== "matrix" || this.groupBy === groupBy) {
        return;
      }
      const cardExtra = this.drawerTrackExtra();
      trackRedemptionAction("matrix_groupby_change", this.appliedFilters, {
        ...cardExtra,
        groupBy,
        previousGroupBy: this.groupBy,
        context: {
          ...cardExtra.context,
          group_by: groupBy,
          previous_group_by: this.groupBy,
        },
      });
      this.groupBy = groupBy;
      this.page = 1;
      this.sort = { column: "units", dir: "desc" };
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  changePage = async (page: number) => {
    try {
      const nextPage = Math.max(1, page);
      const cardExtra = this.drawerTrackExtra();
      trackRedemptionAction("table_page_change", this.appliedFilters, {
        ...cardExtra,
        page: nextPage,
        pageSize: this.pageSize,
        context: {
          ...cardExtra.context,
          page: nextPage,
          page_size: this.pageSize,
        },
      });
      this.page = nextPage;
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  changePageSize = async (pageSize: number) => {
    try {
      const nextSize = clampRedemptionPageSize(pageSize);
      const cardExtra = this.drawerTrackExtra();
      trackRedemptionAction("table_page_size_change", this.appliedFilters, {
        ...cardExtra,
        pageSize: nextSize,
        previousPageSize: this.pageSize,
        context: {
          ...cardExtra.context,
          page_size: nextSize,
          previous_page_size: this.pageSize,
        },
      });
      this.pageSize = nextSize;
      this.page = 1;
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  sortWhitelist = (): readonly string[] => {
    if (this.drawerKind === "journey") {
      return REDEMPTION_JOURNEY_SORT;
    }
    if (this.drawerKind === "matrix") {
      return REDEMPTION_AGG_SORT;
    }
    return sortWhitelistForView(this.productView);
  };

  changeSort = async (column: string) => {
    try {
      const whitelist = this.sortWhitelist();
      if (!whitelist.includes(column)) {
        return;
      }
      const dir =
        this.sort?.column === column && this.sort.dir === "desc"
          ? "asc"
          : "desc";
      const cardExtra = this.drawerTrackExtra();
      trackRedemptionAction("table_sort", this.appliedFilters, {
        ...cardExtra,
        sortColumn: column,
        sortDir: dir,
        context: {
          ...cardExtra.context,
          sort_column: column,
          sort_dir: dir,
        },
      });
      this.sort = { column, dir };
      this.page = 1;
      await this.fetchDrawer();
    } catch (error) {
      throw error;
    }
  };

  fetchDrawer = async () => {
    const requestId = ++this.drawerSeq;
    const kind = this.drawerKind;
    const requestedMetricId = this.journeyMetricId;
    const requestedProductId = this.productId;
    const requestedView = this.productView;
    const requestedGroupBy = this.groupBy;

    try {
      if (!this.drawerOpen || !kind) {
        return;
      }

      runInAction(() => {
        this.drawerLoading = true;
        this.drawerError = null;
      });

      const filters = toRedemptionFilters(this.appliedFilters);
      const pageSize = clampRedemptionPageSize(this.pageSize);
      const shared: RedemptionFilters = {
        ...filters,
        page: this.page,
        page_size: pageSize,
      };

      if (kind === "journey") {
        if (!requestedMetricId) {
          return;
        }
        const sort = sortIfAllowed(this.sort, REDEMPTION_JOURNEY_SORT);
        const result = await this.analyticsAPI.getJourneyDrilldown(
          requestedMetricId,
          {
            ...shared,
            ...(sort ? { sort } : {}),
          },
        );
        if (requestId !== this.drawerSeq) {
          return;
        }
        if (
          this.drawerKind !== "journey" ||
          this.journeyMetricId !== requestedMetricId
        ) {
          return;
        }
        const stageId = result?.data?.stageId;
        if (
          stageId &&
          requestedMetricId !== `journey:${stageId}` &&
          result.meta?.metricId !== requestedMetricId
        ) {
          return;
        }
        runInAction(() => {
          this.journeyResult = result;
          this.productResult = null;
          this.matrixResult = null;
        });
        return;
      }

      if (kind === "product") {
        if (requestedProductId == null) {
          return;
        }
        const sort = sortIfAllowed(
          this.sort,
          sortWhitelistForView(requestedView),
        );
        const result = await this.analyticsAPI.getProductDrilldown(
          requestedProductId,
          {
            ...shared,
            view: requestedView,
            ...(sort ? { sort } : {}),
          },
        );
        if (requestId !== this.drawerSeq) {
          return;
        }
        if (
          this.drawerKind !== "product" ||
          this.productId !== requestedProductId ||
          this.productView !== requestedView
        ) {
          return;
        }
        runInAction(() => {
          this.productResult = result;
          this.journeyResult = null;
          this.matrixResult = null;
        });
        return;
      }

      const sort = sortIfAllowed(this.sort, REDEMPTION_AGG_SORT);
      const result = await this.analyticsAPI.getVariantMatrix({
        ...shared,
        group_by: requestedGroupBy,
        ...(sort ? { sort } : {}),
      });
      if (requestId !== this.drawerSeq) {
        return;
      }
      if (this.drawerKind !== "matrix" || this.groupBy !== requestedGroupBy) {
        return;
      }
      runInAction(() => {
        this.matrixResult = result;
        this.journeyResult = null;
        this.productResult = null;
      });
    } catch (error) {
      if (requestId !== this.drawerSeq) {
        return;
      }
      runInAction(() => {
        this.drawerError = getApiErrorMessage(
          error,
          kind === "matrix"
            ? "Failed to fetch ops redemptions variant matrix."
            : "Failed to fetch ops redemptions drilldown.",
        );
        this.journeyResult = null;
        this.productResult = null;
        this.matrixResult = null;
      });
    } finally {
      if (requestId === this.drawerSeq) {
        runInAction(() => {
          this.drawerLoading = false;
        });
      }
    }
  };

  exportCsv = async (fallbackName: string) => {
    try {
      if (!this.drawerOpen || !this.drawerKind) {
        return;
      }
      if (this.drawerKind === "journey" && !this.journeyMetricId) {
        return;
      }
      if (this.drawerKind === "product" && this.productId == null) {
        return;
      }

      const cardExtra = this.drawerTrackExtra();
      trackRedemptionAction("export_csv", this.appliedFilters, {
        ...cardExtra,
        context: {
          ...cardExtra.context,
          export_type: "csv",
          sort_column: this.sort?.column,
          sort_dir: this.sort?.dir,
        },
      });

      this.exportLoading = true;
      this.exportError = null;

      const filters = toRedemptionFilters(this.appliedFilters);
      const sort = sortIfAllowed(this.sort, this.sortWhitelist());
      const body: RedemptionFilters = {
        ...filters,
        ...(sort ? { sort } : {}),
      };

      let result: APIBlobResponse;
      if (this.drawerKind === "journey" && this.journeyMetricId) {
        result = await this.analyticsAPI.exportJourneyCsv(
          this.journeyMetricId,
          body,
        );
      } else if (this.drawerKind === "product" && this.productId != null) {
        result = await this.analyticsAPI.exportProductCsv(
          this.productId,
          body,
          this.productView,
        );
      } else {
        result = await this.analyticsAPI.exportVariantMatrixCsv(
          body,
          this.groupBy,
        );
      }

      if (!result.data) {
        throw new Error("Download failed");
      }

      triggerCsvDownload(result.data, result.filename || fallbackName);
    } catch (error) {
      runInAction(() => {
        this.exportError = getApiErrorMessage(error, "Failed to export CSV.");
      });
    } finally {
      runInAction(() => {
        this.exportLoading = false;
      });
    }
  };
}
